import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { ArrowLeft, MapPin, Truck, CreditCard, FileText, User, Package, Database, ChevronDown, ChevronUp, DollarSign, CheckCircle2, Clock, AlertCircle, Printer, Edit2, Trash2, Save, X, Plus, Mail, Download } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';
import ProductSearchInput from '../components/ProductSearchInput';

const FieldGrid = ({ fields }) => {
  const validFields = fields.filter(f => f.value && f.value !== '0' && f.value !== '$0.00');
  if (validFields.length === 0) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {validFields.map((f, i) => (
        <div key={i}>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{f.label}</p>
          <p className="text-sm font-medium mt-0.5">{f.value}</p>
        </div>
      ))}
    </div>
  );
};

const Section = ({ title, icon: Icon, fields }) => {
  const validFields = fields.filter(f => f.value && f.value !== '0' && f.value !== '$0.00');
  if (validFields.length === 0) return null;
  return (
    <div className="bg-card rounded-lg border border-border p-5">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
        {Icon && <Icon className="h-4 w-4" />} {title}
      </h3>
      <FieldGrid fields={fields} />
    </div>
  );
};

// eslint-disable-next-line no-unused-vars -- debug-only helper; kept for future use
const RawDataSection = ({ rawData }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rawData || Object.keys(rawData).length === 0) return null;

  const entries = Object.entries(rawData).filter(([, v]) => v && String(v).trim());

  return (
    <div className="bg-card rounded-lg border border-border">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-muted/30 transition-colors"
        data-testid="raw-data-toggle"
      >
        <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
          <Database className="h-4 w-4" /> All Import Data ({entries.length} fields)
        </h3>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <div className="px-5 pb-5 border-t border-border pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {entries.map(([key, value]) => (
              <div key={key} className="bg-muted/30 rounded px-3 py-2">
                <p className="text-xs text-muted-foreground font-mono">{key}</p>
                <p className="text-sm font-medium mt-0.5 break-words">{String(value)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentSection = ({ order, onPaymentUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [_paymentMethod, _setPaymentMethod] = useState(order.payment_method || '');
  const [saving, setSaving] = useState(false);

  const isPaid = order.payment_status === 'completed' || order.payment_status === 'paid';

  const handleMarkPaid = async (method) => {
    setSaving(true);
    try {
      await api.updateSalesOrderPayment(order.id, {
        payment_status: 'completed',
        payment_method: method,
      });
      onPaymentUpdate();
      setEditing(false);
    } catch (err) {
      alert('Failed to update payment: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleMarkUnpaid = async () => {
    setSaving(true);
    try {
      await api.updateSalesOrderPayment(order.id, {
        payment_status: 'pending',
        payment_method: '',
      });
      onPaymentUpdate();
      setEditing(false);
    } catch (err) {
      alert('Failed to update payment: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-5" data-testid="payment-section">
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
        <DollarSign className="h-4 w-4" /> Payment
      </h3>

      <div className="space-y-4">
        {/* Current Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isPaid ? (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-semibold text-base">Paid</span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-yellow-600">
                <Clock className="h-5 w-5" />
                <span className="font-semibold text-base">Unpaid</span>
              </div>
            )}
            {isPaid && order.payment_method && (
              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 capitalize" data-testid="payment-method-badge">
                {order.payment_method === 'square' ? 'Square (Credit Card)' : order.payment_method}
              </span>
            )}
          </div>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-sm px-3 py-1.5 border border-input rounded-md hover:bg-muted transition-colors"
              data-testid="edit-payment-btn"
            >
              {isPaid ? 'Change' : 'Mark as Paid'}
            </button>
          )}
        </div>

        {/* Square Fee Display */}
        {isPaid && order.payment_method === 'square' && order.square_fee > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-1" data-testid="square-fee-display">
            <div className="flex justify-between text-sm">
              <span className="text-amber-800">Order Total</span>
              <span className="font-medium">${(order.total || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-amber-800">Square Fee (3.5% + $0.15)</span>
              <span className="font-medium text-amber-700">${(order.square_fee || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold pt-1 border-t border-amber-200">
              <span className="text-amber-900">Total Charged</span>
              <span>${(order.total_charged || 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Edit Payment Mode */}
        {editing && (
          <div className="border border-border rounded-md p-4 space-y-3 bg-muted/30" data-testid="payment-edit-form">
            <p className="text-sm font-medium">Select payment method:</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'cash', label: 'Cash' },
                { id: 'check', label: 'Check' },
                { id: 'square', label: 'Square (Credit Card)' },
                { id: 'other', label: 'Other' },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleMarkPaid(m.id)}
                  disabled={saving}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md border transition-colors
                    ${m.id === 'square' ? 'border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800' : 'border-input hover:bg-muted'}
                    disabled:opacity-50`}
                  data-testid={`pay-method-${m.id}`}
                >
                  {m.label}
                  {m.id === 'square' && (
                    <p className="text-xs font-normal mt-0.5 text-amber-600">+3.5% + $0.15 fee</p>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-2">
              {isPaid && (
                <button
                  onClick={handleMarkUnpaid}
                  disabled={saving}
                  className="text-sm px-3 py-1.5 border border-red-200 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50"
                  data-testid="mark-unpaid-btn"
                >
                  Mark as Unpaid
                </button>
              )}
              <button
                onClick={() => setEditing(false)}
                className="text-sm px-3 py-1.5 border border-input rounded-md hover:bg-muted"
                data-testid="cancel-payment-edit"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SalesOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [emailing, setEmailing] = useState(false);

  const SO_EDIT_KEY = `webb_so_edit_${id}`;
  const editSaved = loadDraft(SO_EDIT_KEY);

  useAutosave(SO_EDIT_KEY, editing ? { editing, editData } : null);

  const loadOrder = async () => {
    try {
      const data = await api.request(`/api/sales-orders/${id}/detail`);
      setOrder(data);
    } catch (err) {
      console.error('Failed to load order', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
    // Restore edit state if autosaved
    if (editSaved?.editing && editSaved?.editData) {
      setEditData(editSaved.editData);
      setEditing(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when id changes; saved edit data is a one-shot restore
  }, [id]);

  const startEdit = () => {
    const toDateInput = (d) => {
      if (!d) return '';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return '';
      return dt.toISOString().split('T')[0];
    };
    setEditData({
      customer_name: order.customer_name || '',
      contact_name: order.contact_name || '',
      phone: order.phone || '',
      email: order.email || '',
      lease_name: order.lease_name || '',
      sales_rep: order.sales_rep || '',
      remarks: order.remarks || '',
      status: order.status || 'draft',
      freight: order.freight || 0,
      order_date: toDateInput(order.order_date || order.created_at),
      due_date: toDateInput(order.due_date),
      requested_ship_date: toDateInput(order.requested_ship_date),
      line_items: (order.line_items || []).map(item => ({
        variant_name: item.variant_name || '',
        sku: item.sku || '',
        quantity: item.quantity || 0,
        unit_price: item.unit_price || 0,
        discount: item.discount || 0,
        total: item.total || 0,
        taxable: item.taxable !== undefined ? item.taxable : (item.is_taxable !== undefined ? item.is_taxable : true),
        variant_id: item.variant_id || '',
        description: item.description || '',
        uom: item.uom || '',
      })),
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.request(`/api/sales-orders/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      setEditing(false);
      clearDraft(SO_EDIT_KEY);
      loadOrder();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete order ${order.order_number}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.request(`/api/sales-orders/${id}`, { method: 'DELETE' });
      navigate('/sales-orders');
    } catch (err) {
      alert('Failed to delete: ' + err.message);
      setDeleting(false);
    }
  };

  const handleConfirmDraft = async () => {
    if (!window.confirm(`Confirm order ${order.order_number}? This will deduct inventory and make the order active.`)) return;
    setConfirming(true);
    try {
      const updated = await api.request(`/api/sales-orders/${id}/confirm`, { method: 'POST' });
      setOrder(updated);
      alert(`Order ${order.order_number} confirmed! Inventory has been deducted.`);
    } catch (err) {
      alert('Failed to confirm: ' + err.message);
    } finally {
      setConfirming(false);
    }
  };


  const handleDownloadPDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-orders/${id}/pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
      });
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${order.order_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF: ' + err.message);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) { alert('Please enter an email address'); return; }
    setEmailing(true);
    try {
      await api.request(`/api/sales-orders/${id}/email`, {
        method: 'POST',
        body: JSON.stringify({ email: emailTo, message: emailMsg }),
      });
      alert(`Invoice emailed to ${emailTo}`);
      setShowEmailModal(false);
      setEmailMsg('');
    } catch (err) {
      const msg = err.message || 'Unknown error';
      if (msg.includes('500') || msg.includes('SMTP') || msg.includes('not configured')) {
        alert('Email failed. Please ensure SMTP_FROM and SMTP_APP_PASSWORD are set in your deployment environment variables (Railway → Service → Variables).\n\nError: ' + msg);
      } else {
        alert('Failed to send: ' + msg);
      }
    } finally {
      setEmailing(false);
    }
  };

  const updateLineItem = (index, field, value) => {
    setEditData(prev => {
      const items = [...prev.line_items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price' || field === 'discount') {
        const qty = parseFloat(items[index].quantity) || 0;
        const price = parseFloat(items[index].unit_price) || 0;
        const disc = parseFloat(items[index].discount) || 0;
        items[index].total = Math.round((qty * price - disc) * 100) / 100;
      }
      return { ...prev, line_items: items };
    });
  };

  const removeLineItem = (index) => {
    setEditData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));
  };

  const addLineItem = () => {
    setEditData(prev => ({
      ...prev,
      line_items: [{
        variant_name: '', sku: '', quantity: 1, unit_price: 0, discount: 0, total: 0, taxable: true, variant_id: '', description: '', uom: '',
      }, ...prev.line_items],
    }));
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
  if (!order) return <div className="p-8 text-center text-red-600">Order not found</div>;

  const statusColor = {
    completed: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    draft: 'bg-gray-100 text-gray-600',
    cancelled: 'bg-red-100 text-red-700',
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

  return (
    <div className="space-y-6" data-testid="sales-order-detail">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-md" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-2xl font-semibold">{order.order_number}</h2>
          <p className="text-muted-foreground">Sales Order</p>
        </div>
        <span className={`px-3 py-1 text-sm font-medium rounded-full ${statusColor[order.status] || 'bg-gray-100'}`} data-testid="order-status">
          {order.status}
        </span>
        {/* Return status badge — red, prominent */}
        {order.return_status === 'fully_returned' && (
          <span className="px-3 py-1 text-sm font-bold rounded-full bg-red-100 text-red-700 border border-red-300" data-testid="return-status-header">
            Returned
          </span>
        )}
        {order.return_status === 'partially_returned' && (
          <span className="px-3 py-1 text-sm font-bold rounded-full bg-red-100 text-red-700 border border-red-300" data-testid="return-status-header">
            Partially Returned
          </span>
        )}
        {!editing && (
          <>
            <button onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="download-pdf-btn">
              <Download className="h-4 w-4" /> PDF
            </button>
            <button onClick={() => { setEmailTo(order.email || order.customer_email || ''); setShowEmailModal(true); }}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              data-testid="email-invoice-btn">
              <Mail className="h-4 w-4" /> Email
            </button>
            <button onClick={startEdit}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="edit-order-btn">
              <Edit2 className="h-4 w-4" /> Edit
            </button>
            <button onClick={handleDelete} disabled={deleting}
              className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 text-sm font-medium disabled:opacity-50"
              data-testid="delete-order-btn">
              <Trash2 className="h-4 w-4" /> {deleting ? 'Deleting...' : 'Delete'}
            </button>
            {order.status === 'draft' && (
              <button onClick={handleConfirmDraft} disabled={confirming}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                data-testid="confirm-draft-btn">
                <CheckCircle2 className="h-4 w-4" /> {confirming ? 'Confirming...' : 'Confirm Order'}
              </button>
            )}
          </>
        )}
        {editing && (
          <>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium disabled:opacity-50"
              data-testid="save-order-btn">
              <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); clearDraft(SO_EDIT_KEY); }}
              className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
              data-testid="cancel-edit-btn">
              <X className="h-4 w-4" /> Cancel
            </button>
          </>
        )}
        <button onClick={() => navigate(`/sales-orders/${id}/invoice`)}
          className="flex items-center gap-2 px-3 py-2 border rounded-lg hover:bg-muted text-sm font-medium"
          data-testid="print-invoice-btn">
          <Printer className="h-4 w-4" /> Print Invoice
        </button>
      </div>

      {/* Draft Warning Banner */}
      {order.status === 'draft' && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="draft-warning">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">This order is a Draft — it has NOT been entered into the system.</p>
            <p className="text-xs text-amber-600">Inventory has not been deducted and this will not appear on the customer's account statement. Click "Confirm Order" to save it.</p>
          </div>
          <button onClick={handleConfirmDraft} disabled={confirming}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50 flex-shrink-0">
            {confirming ? 'Confirming...' : 'Confirm Order'}
          </button>
        </div>
      )}

      {/* Edit Form */}
      {editing && (
        <div className="bg-card rounded-lg border border-border p-5 space-y-4" data-testid="edit-form">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Edit2 className="h-4 w-4" /> Edit Order
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Customer Name</label>
              <input type="text" value={editData.customer_name}
                onChange={e => setEditData({...editData, customer_name: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-customer" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Picked Up By</label>
              <input type="text" value={editData.contact_name}
                onChange={e => setEditData({...editData, contact_name: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-contact" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Phone</label>
              <input type="text" value={editData.phone}
                onChange={e => setEditData({...editData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-phone" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Email</label>
              <input type="text" value={editData.email}
                onChange={e => setEditData({...editData, email: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-email" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Lease Name</label>
              <input type="text" value={editData.lease_name}
                onChange={e => setEditData({...editData, lease_name: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-lease" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Sales Rep</label>
              <input type="text" value={editData.sales_rep}
                onChange={e => setEditData({...editData, sales_rep: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-sales-rep" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select value={editData.status}
                onChange={e => setEditData({...editData, status: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-status">
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="quote">Quote</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Freight ($)</label>
              <input type="number" step="0.01" value={editData.freight}
                onChange={e => setEditData({...editData, freight: parseFloat(e.target.value) || 0})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-freight" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Order Date</label>
              <input type="date" value={editData.order_date || ''}
                onChange={e => setEditData({...editData, order_date: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-order-date" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Due Date</label>
              <input type="date" value={editData.due_date || ''}
                onChange={e => setEditData({...editData, due_date: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-due-date" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Requested Ship Date</label>
              <input type="date" value={editData.requested_ship_date || ''}
                onChange={e => setEditData({...editData, requested_ship_date: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="edit-ship-date" />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className="block text-xs text-muted-foreground mb-1">Remarks</label>
              <textarea value={editData.remarks}
                onChange={e => setEditData({...editData, remarks: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm h-16" data-testid="edit-remarks" />
            </div>
          </div>

          {/* Editable Line Items */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase">Line Items</h4>
              <button onClick={addLineItem} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid="add-line-item">
                <Plus className="h-3 w-3" /> Add Item
              </button>
            </div>
            <div className="space-y-2">
              {editData.line_items?.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-muted/30 rounded-md p-2" data-testid={`edit-line-${i}`}>
                  <div className="col-span-3">
                    <ProductSearchInput
                      value={item.variant_name}
                      onChange={(val) => updateLineItem(i, 'variant_name', val)}
                      onSelect={(p) => {
                        const items = [...editData.line_items];
                        items[i] = {
                          ...items[i],
                          variant_name: p.name || p.variant_name || '',
                          sku: p.sku || '',
                          unit_price: p.unit_price || 0,
                          variant_id: p.id || p.variant_id || '',
                          taxable: p.is_taxable !== undefined ? p.is_taxable : true,
                          quantity: items[i].quantity || 1,
                          total: Math.round(((items[i].quantity || 1) * (p.unit_price || 0)) * 100) / 100,
                        };
                        setEditData({ ...editData, line_items: items });
                      }}
                      placeholder="Search by product name..."
                    />
                  </div>
                  <div className="col-span-2">
                    <input type="text" placeholder="SKU" value={item.sku}
                      onChange={e => updateLineItem(i, 'sku', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm font-mono" />
                  </div>
                  <div className="col-span-1">
                    <input type="number" placeholder="Qty" value={item.quantity}
                      onChange={e => updateLineItem(i, 'quantity', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm text-right" />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" placeholder="Price" value={item.unit_price}
                      onChange={e => updateLineItem(i, 'unit_price', e.target.value)}
                      className="w-full px-2 py-1.5 border border-input rounded text-sm text-right" />
                  </div>
                  <div className="col-span-1 flex items-center justify-center">
                    <button onClick={() => {
                      const items = [...editData.line_items];
                      items[i] = { ...items[i], taxable: !items[i].taxable };
                      setEditData({...editData, line_items: items});
                    }}
                      className={`px-2 py-0.5 text-xs rounded-full ${item.taxable !== false ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
                      data-testid={`edit-tax-${i}`}>
                      {item.taxable !== false ? 'Tax' : 'No Tax'}
                    </button>
                  </div>
                  <div className="col-span-2 text-right text-sm font-medium tabular-nums">
                    ${(item.total || 0).toFixed(2)}
                  </div>
                  <div className="col-span-1 text-right">
                    <button onClick={() => removeLineItem(i)} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600"
                      data-testid={`remove-line-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right mt-2 text-sm font-semibold">
              Subtotal: ${editData.line_items?.reduce((s, i) => s + (i.total || 0), 0).toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Payment Section */}
      <PaymentSection order={order} onPaymentUpdate={loadOrder} />

      <Section title="Order Information" icon={FileText} fields={[
        { label: 'Order Date', value: fmtDate(order.order_date || order.created_at) },
        { label: 'Lease Name', value: order.lease_name },
        { label: 'Invoiced Date', value: order.invoiced_date },
        { label: 'Due Date', value: order.due_date },
        { label: 'Date Paid', value: order.date_paid },
        { label: 'Requested Ship Date', value: order.requested_ship_date },
        { label: 'PO Number', value: order.po_number },
        { label: 'Sales Rep', value: order.sales_rep },
        { label: 'Location', value: order.location },
        { label: 'Pricing Scheme', value: order.pricing_scheme },
        { label: 'Currency', value: order.currency },
        { label: 'Payment Terms', value: order.payment_terms },
        { label: 'Amount Paid', value: order.amount_paid ? `$${order.amount_paid.toFixed(2)}` : '' },
        { label: 'Inventory Status', value: order.inventory_status },
        { label: 'Is Quote', value: order.is_quote ? 'Yes' : '' },
        { label: 'Remarks', value: order.remarks },
      ]} />

      <Section title="Customer" icon={User} fields={[
        { label: 'Customer', value: order.customer_name },
        { label: 'Picked Up By', value: order.contact_name },
        { label: 'Phone', value: order.phone },
        { label: 'Email', value: order.email },
      ]} />

      <Section title="Billing Address" icon={MapPin} fields={[
        { label: 'Address', value: [order.billing_address1, order.billing_address2].filter(Boolean).join(', ') },
        { label: 'City', value: order.billing_city },
        { label: 'State', value: order.billing_state },
        { label: 'Postal Code', value: order.billing_postal_code },
        { label: 'Country', value: order.billing_country },
        { label: 'Remarks', value: order.billing_remarks },
      ]} />

      <Section title="Shipping" icon={Truck} fields={[
        { label: 'Ship To Company', value: order.ship_to_company },
        { label: 'Address', value: [order.shipping_address1, order.shipping_address2].filter(Boolean).join(', ') },
        { label: 'City', value: order.shipping_city },
        { label: 'State', value: order.shipping_state },
        { label: 'Postal Code', value: order.shipping_postal_code },
        { label: 'Country', value: order.shipping_country },
        { label: 'Carrier', value: order.shipping_carrier },
        { label: 'Tracking #', value: order.shipping_tracking_number },
        { label: 'Remarks', value: order.shipping_remarks },
      ]} />

      <Section title="Tax Information" icon={CreditCard} fields={[
        { label: 'Taxing Scheme', value: order.taxing_scheme },
        { label: order.tax1_name || 'Tax 1', value: order.tax1_rate ? `${order.tax1_rate}%` : '' },
        { label: order.tax2_name || 'Tax 2', value: order.tax2_rate ? `${order.tax2_rate}%` : '' },
        { label: 'Non-Customer Cost', value: order.non_customer_cost ? `$${order.non_customer_cost.toFixed(2)}` : '' },
      ]} />

      {/* Line Items */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
            <Package className="h-4 w-4" /> Line Items ({order.line_items?.length || 0})
          </h3>
          {order.return_status && (
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
              order.return_status === 'fully_returned' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
            }`} data-testid="return-status-badge">
              {order.return_status === 'fully_returned' ? 'Fully Returned' : 'Partially Returned'}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">Product</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground">SKU</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Qty</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Returned</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Unit Price</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Discount</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground">Total</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(order.line_items || []).map((item, i) => {
                const returnedQty = item.returned_quantity || 0;
                const isFully = item.is_fully_returned;
                const isPartial = returnedQty > 0 && !isFully;
                return (
                  <tr key={item.variant_id || item.sku || i} className={`hover:bg-muted/30 ${isFully ? 'bg-red-50/40 line-through opacity-70' : isPartial ? 'bg-amber-50/40' : ''}`} data-testid={`line-item-${i}`}>
                    <td className="px-4 py-2.5">
                      {item.variant_id ? (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const v = await api.request(`/api/variants/${item.variant_id}`);
                              if (v?.product_id) navigate(`/products/${v.product_id}`);
                              else alert('Product link unavailable for this line.');
                            } catch (e) {
                              alert('Could not open product: ' + e.message);
                            }
                          }}
                          className="text-sm font-medium text-primary hover:underline text-left"
                          data-testid={`open-product-${i}`}
                          title="Open product page"
                        >
                          {item.variant_name}
                        </button>
                      ) : (
                        <p className="text-sm font-medium">{item.variant_name}</p>
                      )}
                      {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                      {item.uom && <p className="text-xs text-muted-foreground">UOM: {item.uom}</p>}
                      {item.serials && <p className="text-xs text-muted-foreground">S/N: {item.serials}</p>}
                      {item.last_return_number && <p className="text-xs text-red-600 mt-0.5">via {item.last_return_number}</p>}
                      {/* R15: Warranty claim status — show only for the 4 AEI SKUs and only
                          when the answer was explicitly captured (true/false), never on the
                          default `null` that every other line item carries. */}
                      {['01249', '5601864', '5601863', '5601865'].includes(item.sku) && typeof item.warranty_original_returned === 'boolean' && (
                        <p className={`text-xs mt-0.5 ${item.warranty_original_returned ? 'text-green-700' : 'text-amber-700'}`} data-testid={`warranty-status-${i}`}>
                          Warranty: {item.warranty_original_returned ? 'Original returned ✓' : 'Original NOT returned'}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-sm font-mono">{item.sku || '\u2014'}</td>
                    <td className="px-4 py-2.5 text-sm text-right">{item.quantity}</td>
                    <td className={`px-4 py-2.5 text-sm text-right ${returnedQty > 0 ? 'font-bold text-red-600' : 'text-muted-foreground'}`}>{returnedQty}</td>
                    <td className="px-4 py-2.5 text-sm text-right">${(item.unit_price || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-sm text-right">{item.discount ? `$${item.discount.toFixed(2)}` : '\u2014'}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium">${(item.total || 0).toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-center">
                      {isFully ? (
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-red-100 text-red-700 font-medium">RETURNED</span>
                      ) : isPartial ? (
                        <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 font-medium">PARTIAL</span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="max-w-xs ml-auto space-y-1">
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(order.subtotal || 0).toFixed(2)}</span></div>
            {order.freight > 0 && <div className="flex justify-between text-sm"><span>Freight</span><span>${order.freight.toFixed(2)}</span></div>}
            {order.tax > 0 && <div className="flex justify-between text-sm"><span>Tax</span><span>${order.tax.toFixed(2)}</span></div>}
            <div className="flex justify-between text-base font-semibold pt-1 border-t border-border">
              <span>Total</span><span>${(order.total || 0).toFixed(2)}</span>
            </div>
            {order.square_fee > 0 && (
              <>
                <div className="flex justify-between text-sm text-amber-700 pt-1">
                  <span>Square Fee</span><span>+${(order.square_fee).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-semibold text-amber-900">
                  <span>Total Charged</span><span>${(order.total_charged || 0).toFixed(2)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Raw Import Data - shows every field from the original CSV */}
      {order.raw_data && Object.keys(order.raw_data).length > 0 && (
        <Section title="Additional Import Data" icon={Database} fields={
          Object.entries(order.raw_data)
            .filter(([, v]) => v && String(v).trim())
            .map(([key, value]) => ({ label: key, value: String(value) }))
        } />
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="email-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><Mail className="h-5 w-5" /> Email Invoice</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">To Email</label>
                <input type="email" value={emailTo} onChange={e => setEmailTo(e.target.value)}
                  placeholder="customer@email.com"
                  className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="email-to-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Additional Message (optional)</label>
                <textarea value={emailMsg} onChange={e => setEmailMsg(e.target.value)}
                  placeholder="Add a note to the email..."
                  className="w-full px-3 py-2 border border-input rounded-md text-sm h-20" data-testid="email-msg-input" />
              </div>
              <p className="text-xs text-muted-foreground">PDF invoice for {order.order_number} (${(order.total || 0).toFixed(2)}) will be attached.</p>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowEmailModal(false)} className="px-4 py-2 border rounded-md text-sm hover:bg-muted">Cancel</button>
              <button onClick={handleSendEmail} disabled={emailing}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
                data-testid="send-email-btn">
                {emailing ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesOrderDetail;
